export const TAG_HALFTIME_START = 'halftime_start';
export const TAG_HALFTIME_END = 'halftime_end';
export const TAG_PENALTY_START = 'penalty_shootout_start';
export const TAG_PENALTY_END = 'penalty_shootout_end';
export const TAG_RACE_START = 'race_start';
export const TAG_RACE_END = 'race_end';
export const TAG_FIRST_RUN_START = '1st_run_start';
export const TAG_SECOND_RUN_START = '2nd_run_start';
export const TAG_OVERTIME_START = 'overtime_start';
export const TAG_OVERTIME_END = 'overtime_end';
export const TAG_THIRD_START = 'third_start';
export const TAG_THIRD_END = 'third_end';
export const TAG_PLAY_BALL = 'play_ball';
export const TAG_GAME_BALL = 'game_ball';
export const TAG_INNING_START = 'inning_start';
export const TAG_INNING_MID = 'inning_mid';
export const TAG_INNING_END = 'inning_end';

export const INTERVAL_TYPE_HALFTIME = 'ht';
export const INTERVAL_TYPE_PENALTY = 'penalty';

export const INTERVAL_TYPE_RACE = 'race';
export const INTERVAL_TYPE_FIRST_RUN = 'first_run';
export const INTERVAL_TYPE_SECOND_RUN = 'second_run';
export const INTERVAL_TYPE_OVERTIME = 'overtime';
export const INTERVAL_TYPE_THIRD = 'third';
export const INTERVAL_TYPE_BALL_GAME = 'ball_game';
export const INTERVAL_TYPE_BALL_GAME_INNING = 'ball_game_inning';
export const INTERVAL_TYPE_BALL_GAME_PRE_MID = 'ball_game_pre_mid';
export const INTERVAL_TYPE_BALL_GAME_POST_MID = 'ball_game_post_mid';

export const INTERVAL_TEMPLATES = {
	[INTERVAL_TYPE_HALFTIME]: {
		startTag: TAG_HALFTIME_START,
		endTag:   TAG_HALFTIME_END,
	},
	[INTERVAL_TYPE_PENALTY]: {
		startTag: TAG_PENALTY_START,
		endTag:   TAG_PENALTY_END,
	},
	[INTERVAL_TYPE_RACE]: {
		startTag: TAG_RACE_START,
		endTag:   TAG_RACE_END,
	},
	[INTERVAL_TYPE_FIRST_RUN]: {
		startTag: TAG_FIRST_RUN_START,
		endTag:   TAG_SECOND_RUN_START,
	},
	[INTERVAL_TYPE_SECOND_RUN]: {
		startTag: TAG_SECOND_RUN_START,
		endTag:   TAG_RACE_END,
	},
	[INTERVAL_TYPE_THIRD]: {
		startTag: TAG_THIRD_START,
		endTag:   TAG_THIRD_END,
	},
	[INTERVAL_TYPE_OVERTIME]: {
		startTag: TAG_OVERTIME_START,
		endTag:   TAG_OVERTIME_END,
	},
	[INTERVAL_TYPE_BALL_GAME]: {
		startTag: TAG_PLAY_BALL,
		endTag:   TAG_GAME_BALL,
	},
	[INTERVAL_TYPE_BALL_GAME_INNING]: {
		startTag: TAG_INNING_START,
		endTag:   TAG_INNING_END,
	},
	[INTERVAL_TYPE_BALL_GAME_PRE_MID]: {
		startTag: TAG_INNING_START,
		endTag:   TAG_INNING_MID,
	},
	[INTERVAL_TYPE_BALL_GAME_POST_MID]: {
		startTag: TAG_INNING_MID,
		endTag:   TAG_INNING_END,
	},
};

/**
 * @param eventTagsSequence {Array<EventTag>}
 *
 * @return {Object} intervals sequence grouped by interval type. Each interval contains a `start` and an `end` timestamp, delimiting the interval boundaries.
 *                  Eg: {
 *                    ht: [
 *                      { start: 1, end: 140 },
 *                      { start: 456, end: 600 },
 *                      { start: 800, end: null },
 *                      { start: null, end: 1000 },
 *                      ...
 *                    ],
 *                    penalty: [
 *                      { start: 700, end: 750 },
 *                      ...
 *                    ],
 *                  }
 */
export function computeIntervals({ eventTagsSequence = [] }) {
	const createInterval = (start, end) => ({ start, end });
	const intervals = {};
	for (const [intervalType, tagTypes] of Object.entries(INTERVAL_TEMPLATES)) {
		const eventTags = eventTagsSequence
			.filter(et => et.tag.name === tagTypes.startTag || et.tag.name === tagTypes.endTag);
		// sort event tags by timestamp
		eventTags.sort((a, b) => a.timestamp - b.timestamp);
		const seq = [];

		for (let i = 0; i < eventTags.length; i++) {
			const current = eventTags[i];
			const next = eventTags[i + 1];

			if (current.tag.name === tagTypes.startTag) {
				// current sequence starts with a "start" tag
				if (next) {
					if (next.tag.name === tagTypes.endTag) {
						// start -> end sequence
						seq.push(createInterval(current.timestamp, next.timestamp));
						i += 1;
					} else {
						// start -> start sequence
						seq.push(createInterval(current.timestamp, null));
					}
				} else {
					// start -> "nothing" sequence
					seq.push(createInterval(current.timestamp, null));
				}
			} else {
				// current sequence starts with an "end" tag
				seq.push(createInterval(null, current.timestamp));
			}
		}

		// add to `intervals` object
		intervals[intervalType] = seq;
	}

	return intervals;
}

/**
 * Returns all the intervals that enclose the provided play position
 *
 * @param intervals
 * @param position
 * @return {Object} - a literal object containing the intervals that are currently 'active' relative to the play position,
 *         grouped by interval type, eg:
 *
 *         {
 *           [INTERVAL_TYPE_RACE]: [{start: 10, 100}],
 *           [INTERVAL_TYPE_FIRST_RUN]: [{start: 20, end: 50}]
 *         }
 */
export function getPositionIntervals({ intervals = {}, position = 0 }) {
	const ret = {};

	for (const [intervalType, intervalsList] of Object.entries(intervals)) {
		ret[intervalType] = intervalsList.filter(interval => (
			position >= interval.start &&
			(interval.end === null || position <= interval.end)
		));
	}

	return ret;
}
